// Pure helpers used by FormSheet — extracted so the title-hoist + invisible
// evaluation can be unit-tested without dragging in the React provider stack.

import type {
    FormElement,
    FormFieldElement,
    FormNotebookElement,
    FormNotebookPage,
    FormSectionElement,
    RecordData,
} from "../core";
import { evalRowInvisible } from "../widgets/rowVisibility";

/**
 * Walk the DSL element tree (incl. nested sections) and return the FIRST
 * field with `widget="record_title"`. Used to hoist the title into the
 * dedicated `.form-sheet__title` slot at the top of the sheet.
 */
export function findFirstRecordTitle(elements: FormElement[]): FormFieldElement | null {
    for (const el of elements) {
        if (el.type === "field" && el.widget === "record_title") return el;
        if (el.type === "section") {
            const nested = findFirstRecordTitle(el.elements);
            if (nested) return nested;
        }
    }
    return null;
}

/**
 * Return a copy of the element tree with the FIRST `record_title` field
 * removed (mirrors `findFirstRecordTitle` traversal). Nested sections are
 * traversed depth-first; only one record_title is ever stripped.
 */
export function stripFirstRecordTitle(elements: FormElement[]): FormElement[] {
    let stripped = false;
    function walk(list: FormElement[]): FormElement[] {
        const out: FormElement[] = [];
        for (const el of list) {
            if (!stripped && el.type === "field" && el.widget === "record_title") {
                stripped = true;
                continue;
            }
            if (el.type === "section") {
                const next: FormSectionElement = { ...el, elements: walk(el.elements) };
                out.push(next);
                continue;
            }
            out.push(el);
        }
        return out;
    }
    return walk(elements);
}

/**
 * Per-record invisible-expression evaluator for sheet elements.
 * Returns TRUE when the element should be hidden for the current draft.
 * Delegates to the shared `evalRowInvisible` (also used by list-row buttons).
 */
export function isElementInvisible(
    el: { invisible?: string | null | undefined },
    record: RecordData,
): boolean {
    return evalRowInvisible(el.invisible, record);
}

/**
 * Walk the form sheet element tree and return the set of field names that
 * are currently invisible for `draft`. A field is invisible when:
 *   - its own `invisible` predicate evaluates true, OR
 *   - it sits inside a section / notebook page whose `invisible` predicate
 *     evaluates true (inheritance — invisible cascades down).
 *
 * Used by the form-state's required-validation pass to skip required-but-
 * invisible fields (e.g. a back-reference `quote_version_id` declared with
 * `invisible="1"` on a child form — the backend auto-injects it through
 * the nested-tuple handler, so the UI shouldn't block save on it).
 */
/**
 * Field names the form-view layout marks `required="1"`. The required `*`
 * marker (FormSheet) already honors the view element's `required`; collecting
 * the same set lets required-validation agree, so a layout-required field
 * actually blocks save instead of only showing a marker. Visibility is handled
 * separately by the caller's invisible-field set, so a required-but-hidden
 * field is still skipped at validation time.
 */
export function collectRequiredFieldNames(elements: FormElement[]): Set<string> {
    const out = new Set<string>();
    walk(elements);
    return out;

    function walk(items: FormElement[]): void {
        for (const el of items) {
            if (el.type === "field") {
                if ((el as FormFieldElement).required === true) out.add((el as FormFieldElement).name);
                continue;
            }
            if (el.type === "section") {
                walk((el as FormSectionElement).elements);
                continue;
            }
            if (el.type === "notebook") {
                for (const page of (el as FormNotebookElement).pages) {
                    walk((page as FormNotebookPage).elements);
                }
            }
        }
    }
}

export function collectInvisibleFieldNames(
    elements: FormElement[],
    draft: RecordData,
): Set<string> {
    const out = new Set<string>();
    walk(elements, false);
    return out;

    function walk(items: FormElement[], inheritedInvisible: boolean): void {
        for (const el of items) {
            const isHidden = inheritedInvisible || ("invisible" in el && isElementInvisible(el, draft));
            if (el.type === "field") {
                if (isHidden) out.add((el as FormFieldElement).name);
                continue;
            }
            if (el.type === "section") {
                walk((el as FormSectionElement).elements, isHidden);
                continue;
            }
            if (el.type === "notebook") {
                for (const page of (el as FormNotebookElement).pages) {
                    const pageHidden = isHidden || isElementInvisible(page as FormNotebookPage, draft);
                    walk((page as FormNotebookPage).elements, pageHidden);
                }
                continue;
            }
            // button / statusbar / property / related — never carry nested
            // form-validatable field trees, no recursion needed.
        }
    }
}
